import { CircularProgress, Grid, IconButton, Box, Typography, Card } from "@mui/material";
import { VisibilityOutlined } from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import MainLayout from "../../layouts/main_layout/main_layout";
import { firestore } from "../../index";
import { MAIN_THEME_COLOR, SECONDARY_THEME_COLOR } from "../../providers/theme/colors/colors";

type GalleryItem = {
  docId: string;
  title: string;
  coverImage: string;
  albumUrl: string;
  programDate?: string;
  createdAt?: string;
};

function normalizeUrl(value?: string) {
  const trimmedValue = value?.trim() || "";

  if (!trimmedValue) {
    return "";
  }

  if (
    trimmedValue.startsWith("http://") ||
    trimmedValue.startsWith("https://") ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
}

export default function GalleryPage() {
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchGalleryItems() {
      try {
        const galleryQuery = query(
          collection(firestore, "gallery"),
          orderBy("programDate", "desc"),
        );
        const snapshot = await getDocs(galleryQuery);
        setGalleryItems(
          snapshot.docs.map((doc) => ({
            docId: doc.id,
            ...(doc.data() as Omit<GalleryItem, "docId">),
            coverImage: normalizeUrl((doc.data() as Omit<GalleryItem, "docId">).coverImage),
            albumUrl: normalizeUrl((doc.data() as Omit<GalleryItem, "docId">).albumUrl),
          })),
        );
      } finally {
        setLoading(false);
      }
    }

    fetchGalleryItems();
  }, []);

  const galleryItemsByYear = useMemo(() => {
    return galleryItems.reduce<Record<string, GalleryItem[]>>((acc, item) => {
      const year = item.programDate?.slice(0, 4);
      if (!year) {
        return acc;
      }
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(item);
      return acc;
    }, {});
  }, [galleryItems]);

  const orderedYears = useMemo(
    () => Object.keys(galleryItemsByYear).sort((a, b) => Number(b) - Number(a)),
    [galleryItemsByYear],
  );

  return (
    <MainLayout>
      <Box my={{ xs: 4, md: 6 }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" fontWeight={800} color={MAIN_THEME_COLOR}>
            Gallery
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={1}>
            Explore programs and photo albums from the diocese.
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : galleryItems.length === 0 ? (
          <Box
            py={8}
            textAlign="center"
            border={`1px dashed ${SECONDARY_THEME_COLOR}`}
            borderRadius={3}
          >
            <Typography variant="h6" fontWeight={700}>
              No gallery items available
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={5}>
            {orderedYears.map((year) => (
              <Box key={year}>
                <Box
                  mb={2.5}
                  px={2}
                  py={1.2}
                  sx={{
                    width: "fit-content",
                    borderRadius: 999,
                    color: "#fff",
                    background: `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
                    boxShadow: "0 10px 24px rgba(64, 162, 227, 0.18)",
                  }}
                >
                  <Typography variant="h5" fontWeight={800}>
                    {year}
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  {galleryItemsByYear[year].map((item) => (
                    <Grid item xs={12} sm={6} md={6} lg={4} key={item.docId}>
                      <Card
                        sx={{
                          height: "100%",
                          p: 2,
                          borderRadius: 4,
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid rgba(56, 136, 194, 0.12)",
                          boxShadow: "0 14px 30px rgba(9, 51, 84, 0.08)",
                          background:
                            "linear-gradient(180deg, rgba(247, 251, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)",
                        }}
                      >
                        <Box
                          sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            aspectRatio: "16 / 10",
                            bgcolor: "#ffffff",
                            border: `1px solid ${SECONDARY_THEME_COLOR}55`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 2,
                          }}
                        >
                          {failedImages[item.docId] || !item.coverImage ? (
                            <Box textAlign="center" px={2}>
                              <Typography variant="body2" fontWeight={700}>
                                Image unavailable
                              </Typography>
                              
                            </Box>
                          ) : (
                            <Box
                              component="img"
                              src={item.coverImage}
                              alt={item.title}
                              loading="lazy"
                              onError={() =>
                                setFailedImages((prev) => ({ ...prev, [item.docId]: true }))
                              }
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                objectPosition: "center center",
                              }}
                            />
                          )}
                        </Box>

                        <Box
                          mt={2}
                          display="flex"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={700}>
                              {item.title}
                            </Typography>
                            {item.programDate ? (
                              <Typography variant="body2" color="text.secondary" mt={0.5}>
                                {new Date(item.programDate).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </Typography>
                            ) : null}
                          </Box>
                          <IconButton
                            color="primary"
                            onClick={() =>
                              window.open(
                                item.albumUrl,
                                "_blank",
                                "noopener,noreferrer",
                              )
                            }
                          >
                            <VisibilityOutlined />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </MainLayout>
  );
}
